import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CloudDone, CloudOutlined, DragIndicator, PushPin } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { type Theme, alpha } from '@mui/material/styles'
import { isChapterSectionHeadingText, stripChapterSectionHeadingPrefix } from '@shared/ast/tocChapterSectionHeading'
import React, { useCallback, useRef } from 'react'

import ChapterContextMenu from '../../../components/ChapterContextMenu'
import { TagDefinition, getTagDisplayName, resolveTagColor } from '../../../components/tags/tagTreeUtils'
import { Book, ContentFile } from '../../../services/api'

interface ChapterHeadingItem {
  text: string
  level: number
  id: string
}

/** Matches MUI `IconButton size="small"` width so TOC heading rows align with publish + drag columns */
const PUBLISH_ICON_SLOT_PX = 34
/** Same width/height as chapter row drag / file-type box */
const TOC_ICON_COL_PX = 20

function tocHeadingTitleColor(theme: Theme, isActive: boolean, isChapterSection: boolean): string {
  const dark = theme.palette.mode === 'dark'
  if (isActive) {
    return isChapterSection
      ? dark
        ? alpha(theme.palette.common.white, 0.72)
        : theme.palette.grey[800]
      : 'var(--sidebar-heading-active)'
  }
  return isChapterSection
    ? dark
      ? alpha(theme.palette.common.white, 0.42)
      : theme.palette.grey[600]
    : 'var(--sidebar-text)'
}

type TocHeadingIconColumnProps = {
  showPublish: boolean
  isChapterSection: boolean
  isActive: boolean
}

function TocHeadingIconColumn({ showPublish, isChapterSection, isActive }: TocHeadingIconColumnProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {showPublish && <Box aria-hidden sx={{ width: PUBLISH_ICON_SLOT_PX, height: TOC_ICON_COL_PX, flexShrink: 0 }} />}
      <Box
        aria-hidden
        sx={{
          width: TOC_ICON_COL_PX,
          height: TOC_ICON_COL_PX,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isChapterSection && <PushPin sx={{ fontSize: 'small', color: 'inherit', opacity: isActive ? 0.9 : 0.55 }} />}
      </Box>
    </Box>
  )
}

interface SortableChapterItemProps {
  chapter: ContentFile
  isSelected: boolean
  /** True while this chapter is being fetched after a TOC row click (drag-handle area shows a spinner). */
  isLoadingSelection?: boolean
  isExpanded: boolean
  book: Book | null
  onSelect: (chapter: ContentFile) => void
  onDelete: (chapter: ContentFile) => void
  onRename: (chapter: ContentFile) => void
  onTag: (chapter: ContentFile) => void
  onPublish: (chapter: ContentFile) => void
  getHeadings: (chapter: ContentFile) => ChapterHeadingItem[]
  onHeadingClick: (chapter: ContentFile, headingBlockId: string) => void
  showPublish?: boolean
  activeHeadingId?: string | null
  tagMatchColor?: string | null
  tagColorMap?: Record<string, string>
  tagRegistry?: Record<string, TagDefinition>
  suppressAutoScroll?: boolean
}

export default function SortableChapterItem({
  chapter,
  isSelected,
  isLoadingSelection = false,
  isExpanded,
  book,
  onSelect,
  onDelete,
  onRename,
  onTag,
  onPublish,
  getHeadings,
  onHeadingClick,
  showPublish = true,
  activeHeadingId,
  tagMatchColor,
  tagColorMap = {},
  tagRegistry = {},
  suppressAutoScroll = false,
}: SortableChapterItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: chapter.filename,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const headings = getHeadings(chapter)
  const isPublished = Boolean(chapter.published)
  const fileTags = chapter.fileTags || []

  const initialScrollDone = useRef(false)
  const combinedRef = useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node)
      if (node && isSelected && !initialScrollDone.current && !suppressAutoScroll) {
        initialScrollDone.current = true
        requestAnimationFrame(() => {
          let scrollParent: HTMLElement | null = node.parentElement
          while (scrollParent) {
            const nodeStyle = getComputedStyle(scrollParent)
            if (nodeStyle.overflowY === 'auto' || nodeStyle.overflowY === 'scroll') {
              break
            }
            scrollParent = scrollParent.parentElement
          }
          if (scrollParent) {
            const parentRect = scrollParent.getBoundingClientRect()
            const nodeRect = node.getBoundingClientRect()
            const targetY = parentRect.top + parentRect.height / 2 - nodeRect.height / 2
            const offset = nodeRect.top - targetY
            scrollParent.scrollBy({ top: offset, behavior: 'instant' })
          } else {
            node.scrollIntoView({ block: 'center', behavior: 'instant' })
          }
        })
      }
    },
    [setNodeRef, isSelected, suppressAutoScroll],
  )

  return (
    <Tooltip
      disableHoverListener={fileTags.length === 0}
      placement={book?.metadata?.direction === 'rtl' ? 'left-start' : 'right-start'}
      disableInteractive
      arrow
      enterDelay={120}
      enterNextDelay={40}
      slotProps={{
        popper: {
          sx: {
            zIndex: (theme) => theme.zIndex.modal - 1,
          },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            bgcolor: 'rgba(14, 18, 26, 0.96)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
            p: 1,
            borderRadius: 1.5,
            maxWidth: 320,
          },
        },
        arrow: {
          sx: {
            color: 'rgba(14, 18, 26, 0.96)',
          },
        },
      }}
      title={
        fileTags.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {fileTags.map((tag) => {
              const c = resolveTagColor(tag, tagColorMap, tagRegistry)
              return (
                <Box
                  key={tag}
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    px: 1,
                    py: 0.45,
                    borderRadius: 1.5,
                    color: 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: c,
                      flex: '0 0 auto',
                    }}
                  />
                  {getTagDisplayName(tagRegistry, tag)}
                </Box>
              )
            })}
          </Box>
        ) : (
          ''
        )
      }
    >
      <Box ref={combinedRef} style={style}>
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 2,
            bgcolor: isSelected ? 'var(--sidebar-selected)' : 'transparent',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            borderInlineStart: tagMatchColor ? `3px solid ${tagMatchColor}` : '3px solid transparent',
            '&:hover': {
              bgcolor: isSelected ? 'var(--sidebar-selected)' : 'var(--bg-hover)',
              '& .drag-handle': { opacity: 1 },
            },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            onClick={() => onSelect(chapter)}
          >
            {showPublish && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onPublish(chapter)
                }}
                sx={{
                  p: 0.5,
                  color: 'var(--icon-default)',
                  '&:hover': {
                    color: 'var(--icon-hover)',
                    bgcolor: 'var(--bg-hover)',
                  },
                }}
                title={isPublished ? 'Published - Click to unpublish' : 'Unpublished - Click to publish'}
              >
                {isPublished ? <CloudDone fontSize="small" /> : <CloudOutlined fontSize="small" />}
              </IconButton>
            )}
            {/* Drag handle area (spinner while chapter is loading) */}
            <Box
              sx={{ position: 'relative', width: TOC_ICON_COL_PX, height: TOC_ICON_COL_PX, flexShrink: 0 }}
              aria-busy={isLoadingSelection}
              aria-label={isLoadingSelection ? 'Loading chapter' : undefined}
            >
              {isLoadingSelection ? (
                <CircularProgress
                  size={16}
                  thickness={4}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    marginLeft: '-8px',
                    marginTop: '-8px',
                    color: 'var(--sidebar-text-muted)',
                  }}
                />
              ) : (
                <DragIndicator
                  className="drag-handle"
                  fontSize="small"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    color: 'var(--sidebar-text-muted)',
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                    '&:hover': { color: 'var(--sidebar-text-muted-hover)' },
                  }}
                  {...attributes}
                  {...listeners}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontFamily: book?.settings.titleFontFamily || 'SBL Hebrew',
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? 'var(--sidebar-selected-text)' : 'var(--sidebar-text)',
                fontSize: '14px',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {chapter.order}. {chapter.title}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <ChapterContextMenu chapter={chapter} onDelete={onDelete} onRename={onRename} onTag={onTag} />
          </Box>
        </Box>

        {!isDragging && isSelected && isExpanded && headings.length > 0 && (
          <Box sx={{ pb: 0.5 }}>
            {headings.map((heading, index) => {
              const isActive = isSelected && activeHeadingId === heading.id
              const isChapterSection = isChapterSectionHeadingText(heading.text)
              const label = stripChapterSectionHeadingPrefix(heading.text).trim() || heading.text
              const bookDir = book?.metadata?.direction || 'ltr'
              return (
                <Box
                  key={index}
                  data-heading-id={heading.id}
                  onClick={() => onHeadingClick(chapter, heading.id)}
                  sx={{
                    px: 2,
                    py: 0.25,
                    cursor: 'pointer',
                    borderRadius: 1,
                    bgcolor:
                      isActive && isChapterSection
                        ? (theme) =>
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.1)
                              : alpha(theme.palette.common.black, 0.08)
                        : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: 'var(--bg-hover)',
                    },
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'row',
                      direction: 'ltr',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 0,
                      width: '100%',
                      color: tocHeadingTitleColor(theme, isActive, isChapterSection),
                    })}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontFamily: book?.settings.titleFontFamily || 'SBL Hebrew',
                        color: 'inherit',
                        fontSize: '14px',
                        lineHeight: 1.3,
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'start',
                        fontWeight: isActive ? 600 : heading.level === 2 ? 500 : 400,
                        direction: bookDir,
                        paddingInlineStart: 2 + (heading.level - 1) * 2,
                      }}
                    >
                      {label}
                    </Typography>
                    <TocHeadingIconColumn
                      showPublish={showPublish}
                      isChapterSection={isChapterSection}
                      isActive={isActive}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Box>
    </Tooltip>
  )
}
